import { cn } from "@/lib/utils";

interface TagDetectionRecord {
  _id: string;
  rawText: string;
  bbox: { x: number; y: number; width: number; height: number };
}

interface StyleResolver {
  color: string;
  fillClassName?: string;
  fillColor?: string;
  badgeText?: string;
}

interface DetectionItemProps<TDetection extends TagDetectionRecord> {
  detection: TDetection;
  archived: boolean;
  rect: { x: number; y: number; width: number; height: number };
  style: StyleResolver;
  editMode: boolean;
  pendingId: string | null;
  isSpacePanActive: boolean;
  selectedDetectionIds: ReadonlySet<string>;
  duplicateSourceDetectionId: string | null;
  highlightedDetectionId?: string | null;
  showInlineLabel: boolean;
  secondaryLabel?: (detection: TDetection) => string | undefined;
  onSelectDetection: (detectionId: string, additive: boolean) => void;
  onStartSpacePan: (event: React.MouseEvent<HTMLElement>) => void;
  onStartDrag: (detection: TDetection, event: React.MouseEvent<HTMLButtonElement>) => void;
}

function DetectionItem<TDetection extends TagDetectionRecord>({
  detection,
  archived,
  rect,
  style,
  editMode,
  pendingId,
  isSpacePanActive,
  selectedDetectionIds,
  duplicateSourceDetectionId,
  highlightedDetectionId,
  showInlineLabel,
  secondaryLabel,
  onSelectDetection,
  onStartSpacePan,
  onStartDrag,
}: DetectionItemProps<TDetection>) {
  const canRenderInlineLabel = showInlineLabel && rect.width >= 72 && rect.height >= 32;
  const inlineFontSize = Math.max(8, Math.min(14, Math.min(rect.height * 0.22, rect.width * 0.12)));
  const detectionId = String(detection._id);
  const isSelected = selectedDetectionIds.has(detectionId);
  const isDuplicateSource = duplicateSourceDetectionId === detectionId;
  const isHighlighted = highlightedDetectionId === detectionId;

  return (
    <button
      key={detectionId}
      type="button"
      disabled={pendingId === detectionId}
      onClick={(event) => {
        event.stopPropagation();
        if (!editMode || isSpacePanActive) {
          return;
        }
        onSelectDetection(detectionId, event.metaKey || event.ctrlKey || event.shiftKey);
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
        if (isSpacePanActive) {
          onStartSpacePan(event);
          return;
        }
        if (event.metaKey || event.ctrlKey || event.shiftKey) {
          return;
        }
        onStartDrag(detection, event);
      }}
      className={cn(
        "pointer-events-auto absolute overflow-hidden rounded-[10px] border-2 text-left shadow-[0_0_0_1px_rgba(15,23,42,0.14)] transition",
        archived ? "border-dashed bg-slate-900/20" : style.fillClassName ?? "bg-transparent",
        isSelected && "z-20 scale-[1.015] ring-4 ring-primary ring-offset-2 ring-offset-background",
        isDuplicateSource && !isSelected && "ring-2 ring-offset-2 ring-offset-background ring-amber-400",
        isHighlighted && "z-10 ring-4 ring-amber-300/80 ring-offset-2 ring-offset-background animate-pulse"
      )}
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
        borderColor: style.color,
        backgroundColor: !archived ? style.fillColor : undefined,
        boxShadow: isSelected
          ? `0 0 0 2px ${style.color}99, 0 0 10px ${style.color}8C, 0 3px 10px rgba(15,23,42,0.34)`
          : `0 0 0 1px ${style.color}33`,
      }}
      aria-label={detection.rawText}
      aria-pressed={isSelected}
    >
      {canRenderInlineLabel ? (
        <div
          className="absolute inset-0 flex flex-col justify-between px-2 py-1.5"
          style={{ fontSize: `${inlineFontSize}px`, lineHeight: 1.1 }}
        >
          <div className="truncate font-semibold uppercase tracking-[0.12em]">
            {detection.rawText}
          </div>
          <div className="truncate font-medium opacity-80">
            {secondaryLabel?.(detection) ?? style.badgeText ?? ""}
          </div>
        </div>
      ) : null}
    </button>
  );
}

export interface DetectionListProps<TDetection extends TagDetectionRecord> {
  activeDetections: TDetection[];
  archivedDetections: TDetection[];
  getStyle: (detection: TDetection) => StyleResolver;
  showInlineLabel: boolean;
  secondaryLabel?: (detection: TDetection) => string | undefined;
  pageRectToScreenRect: (rect: TagDetectionRecord["bbox"]) => {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  dragPreviewRects: Record<string, TagDetectionRecord["bbox"]>;
  editMode: boolean;
  pendingId: string | null;
  isSpacePanActive: boolean;
  selectedDetectionIds: ReadonlySet<string>;
  duplicateSourceDetectionId: string | null;
  highlightedDetectionId?: string | null;
  onSelectDetection: (detectionId: string, additive: boolean) => void;
  onStartSpacePan: (event: React.MouseEvent<HTMLElement>) => void;
  onStartDrag: (detection: TDetection, archived: boolean, event: React.MouseEvent<HTMLButtonElement>) => void;
}

export function DetectionList<TDetection extends TagDetectionRecord>({
  activeDetections,
  archivedDetections,
  getStyle,
  showInlineLabel,
  secondaryLabel,
  pageRectToScreenRect,
  dragPreviewRects,
  editMode,
  pendingId,
  isSpacePanActive,
  selectedDetectionIds,
  duplicateSourceDetectionId,
  highlightedDetectionId,
  onSelectDetection,
  onStartSpacePan,
  onStartDrag,
}: DetectionListProps<TDetection>) {
  return (
    <>
      {activeDetections.map((detection) => (
        <DetectionItem
          key={String(detection._id)}
          detection={detection}
          archived={false}
          rect={pageRectToScreenRect(dragPreviewRects[String(detection._id)] ?? detection.bbox)}
          style={getStyle(detection)}
          editMode={editMode}
          pendingId={pendingId}
          isSpacePanActive={isSpacePanActive}
          selectedDetectionIds={selectedDetectionIds}
          duplicateSourceDetectionId={duplicateSourceDetectionId}
          highlightedDetectionId={highlightedDetectionId}
          showInlineLabel={showInlineLabel}
          secondaryLabel={secondaryLabel}
          onSelectDetection={onSelectDetection}
          onStartSpacePan={onStartSpacePan}
          onStartDrag={(targetDetection, event) => onStartDrag(targetDetection, false, event)}
        />
      ))}
      {archivedDetections.map((detection) => (
        <DetectionItem
          key={String(detection._id)}
          detection={detection}
          archived
          rect={pageRectToScreenRect(dragPreviewRects[String(detection._id)] ?? detection.bbox)}
          style={getStyle(detection)}
          editMode={editMode}
          pendingId={pendingId}
          isSpacePanActive={isSpacePanActive}
          selectedDetectionIds={selectedDetectionIds}
          duplicateSourceDetectionId={duplicateSourceDetectionId}
          highlightedDetectionId={highlightedDetectionId}
          showInlineLabel={showInlineLabel}
          secondaryLabel={secondaryLabel}
          onSelectDetection={onSelectDetection}
          onStartSpacePan={onStartSpacePan}
          onStartDrag={(targetDetection, event) => onStartDrag(targetDetection, true, event)}
        />
      ))}
    </>
  );
}
